import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../lib/api";
import { getApiErrorMessage, normalizeLang } from "../lib/apiErrorMessage";
import { useCreateContact } from "../lib/queries/contacts";

export function InlineContactForm({
  companyId,
  onCreated,
  onCancel,
}: {
  companyId: string;
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const create = useCreateContact(companyId);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (create.isPending) return;
    setError(null);
    try {
      const contact = await create.mutateAsync({
        company_id: companyId,
        full_name: name.trim(),
        email: email.trim(),
      });
      onCreated(contact.id);
    } catch (err) {
      setError(
        err instanceof ApiException
          ? getApiErrorMessage(err, normalizeLang(i18n.language))
          : t("errors.generic"),
      );
    }
  }
  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="space-y-3 rounded-lg border border-slate-200 p-4"
      aria-label={t("request_flow.add_contact")}
    >
      <label className="block" htmlFor={`${id}-name`}>
        {t("request_flow.contact_name")}
        <input
          className="input"
          id={`${id}-name`}
          name="contact_name"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="block" htmlFor={`${id}-email`}>
        {t("request_flow.contact_email")}
        <input
          className="input"
          id={`${id}-email`}
          name="contact_email"
          autoComplete="email"
          type="email"
          spellCheck={false}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      <div className="flex gap-3">
        <button type="submit" disabled={create.isPending} className="btn-primary min-h-11">
          {t("common.save")}
        </button>
        <button type="button" onClick={onCancel} className="btn min-h-11">
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}
